import { generatePkce, PKCE_CHALLENGE_METHOD } from './pkce'

const SCRATCH_KEY = 'teamofone.oidc'

export interface OidcConfig {
  /**
   * Authorization endpoint URL — Authentik calls this `/application/o/authorize/`.
   * For new code prefer the explicit endpoints; `issuer` stays for compat
   * with start-login/end-session URL building.
   */
  issuer: string
  authorizationEndpoint?: string
  tokenEndpoint?: string
  userinfoEndpoint?: string
  logoutEndpoint?: string
  clientId: string
  redirectUri: string
  /** Default 'openid profile email'. */
  scope?: string
}

export interface AuthentikUserInfo {
  sub: string
  email?: string
  email_verified?: boolean
  name?: string
  preferred_username?: string
  groups?: string[]
  [k: string]: unknown
}

export interface OidcTokens {
  accessToken: string
  refreshToken?: string
  idToken?: string
  expiresIn?: number
  returnTo: string
}

export interface OidcRefreshTokens {
  accessToken: string
  refreshToken?: string
  idToken?: string
  expiresIn?: number
}

interface ScratchPayload {
  verifier: string
  state: string
  returnTo: string
  /** Issuer the verifier was generated against — sanity-check on callback. */
  issuer: string
}

export type OidcErrorCode =
  | 'no-scratch'
  | 'state-mismatch'
  | 'issuer-mismatch'
  | 'missing-code'
  | 'token-exchange-failed'
  | 'misconfigured'

export class OidcError extends Error {
  constructor(public readonly code: OidcErrorCode, message: string) {
    super(message)
    this.name = 'OidcError'
  }
}

/**
 * Step 1: kick off the OAuth flow. Generates PKCE + state, stashes them in
 * sessionStorage, then redirects the browser to Authentik's authorize URL.
 */
export async function startLogin(
  cfg: OidcConfig,
  returnTo: string,
): Promise<void> {
  assertConfig(cfg)
  const { verifier, challenge, state } = await generatePkce()
  const scratch: ScratchPayload = { verifier, state, returnTo, issuer: cfg.issuer }
  sessionStorage.setItem(SCRATCH_KEY, JSON.stringify(scratch))

  const url = new URL(authorizeEndpoint(cfg))
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('client_id', cfg.clientId)
  url.searchParams.set('redirect_uri', cfg.redirectUri)
  url.searchParams.set('scope', cfg.scope ?? 'openid profile email')
  url.searchParams.set('state', state)
  url.searchParams.set('code_challenge', challenge)
  url.searchParams.set('code_challenge_method', PKCE_CHALLENGE_METHOD)

  window.location.assign(url.toString())
}

/**
 * Step 2: complete the flow on the callback page. Reads `code` + `state`
 * from `window.location`, validates against the sessionStorage scratch,
 * then POSTs to the token endpoint with the verifier.
 */
export async function completeCallback(cfg: OidcConfig): Promise<OidcTokens> {
  assertConfig(cfg)
  const url = new URL(window.location.href)
  const code = url.searchParams.get('code')
  const state = url.searchParams.get('state')

  if (!code) throw new OidcError('missing-code', 'Authorization code missing from callback URL.')

  const scratchRaw = sessionStorage.getItem(SCRATCH_KEY)
  if (!scratchRaw) {
    throw new OidcError(
      'no-scratch',
      'No PKCE state found — login flow was not started in this browser.',
    )
  }
  const scratch = parseScratch(scratchRaw)
  if (!scratch) {
    throw new OidcError('no-scratch', 'PKCE state was malformed; please retry.')
  }
  if (state !== scratch.state) {
    throw new OidcError('state-mismatch', 'OIDC state does not match — possible tampering.')
  }
  if (scratch.issuer !== cfg.issuer) {
    throw new OidcError('issuer-mismatch', 'Issuer changed mid-flow.')
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: cfg.redirectUri,
    client_id: cfg.clientId,
    code_verifier: scratch.verifier,
  })

  const res = await fetch(tokenEndpoint(cfg), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  if (!res.ok) {
    const errBody = await safeJson(res)
    throw new OidcError(
      'token-exchange-failed',
      `Token exchange failed (${res.status}): ${errBody?.error_description ?? errBody?.error ?? res.statusText}`,
    )
  }
  const json = await safeJson(res)
  if (!json || typeof json.access_token !== 'string') {
    throw new OidcError('token-exchange-failed', 'Token endpoint returned no access_token.')
  }
  const accessToken = json.access_token
  // Clear scratch only after a successful exchange so the user can retry.
  sessionStorage.removeItem(SCRATCH_KEY)

  return {
    accessToken,
    refreshToken:
      typeof json.refresh_token === 'string' ? json.refresh_token : undefined,
    idToken: typeof json.id_token === 'string' ? json.id_token : undefined,
    expiresIn: typeof json.expires_in === 'number' ? json.expires_in : undefined,
    returnTo: scratch.returnTo,
  }
}

export async function refreshAccessToken(
  cfg: OidcConfig,
  refreshToken: string,
): Promise<OidcRefreshTokens> {
  assertConfig(cfg)
  if (!refreshToken) {
    throw new OidcError('token-exchange-failed', 'Refresh token missing.')
  }

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: cfg.clientId,
  })

  const res = await fetch(tokenEndpoint(cfg), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  if (!res.ok) {
    const errBody = await safeJson(res)
    throw new OidcError(
      'token-exchange-failed',
      `Token refresh failed (${res.status}): ${errBody?.error_description ?? errBody?.error ?? res.statusText}`,
    )
  }
  const json = await safeJson(res)
  if (!json || typeof json.access_token !== 'string') {
    throw new OidcError('token-exchange-failed', 'Token endpoint returned no access_token.')
  }

  return {
    accessToken: json.access_token,
    refreshToken:
      typeof json.refresh_token === 'string' ? json.refresh_token : undefined,
    idToken: typeof json.id_token === 'string' ? json.id_token : undefined,
    expiresIn: typeof json.expires_in === 'number' ? json.expires_in : undefined,
  }
}

/**
 * Build the Authentik end-session URL. Logout caller redirects to this
 * after clearing local state. Authentik tolerates a missing client at
 * this endpoint — best-effort cleanup of the SSO cookie.
 */
export function buildEndSessionUrl(
  cfg: OidcConfig,
  postLogoutRedirectUri: string,
): string {
  const endpoint =
    cfg.logoutEndpoint ?? `${cfg.issuer.replace(/\/$/, '')}/end-session/`
  const url = new URL(endpoint)
  url.searchParams.set('post_logout_redirect_uri', postLogoutRedirectUri)
  return url.toString()
}

/** Sanitise `returnTo` — must be a same-origin path, never a full URL. */
export function sanitiseReturnTo(raw: string | null | undefined): string {
  if (!raw) return '/'
  // Block protocol-relative + javascript: + non-paths
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes(':')) return '/'
  return raw
}

// ── internal ──────────────────────────────────────────────────────────────

function assertConfig(cfg: OidcConfig): void {
  if (!cfg.issuer || !cfg.clientId || !cfg.redirectUri) {
    throw new OidcError(
      'misconfigured',
      'OIDC config missing — check VITE_AUTHENTIK_ISSUER_URL / VITE_AUTHENTIK_CLIENT_ID env vars.',
    )
  }
}

function authorizeEndpoint(cfg: OidcConfig): string {
  return cfg.authorizationEndpoint ?? `${cfg.issuer.replace(/\/$/, '')}/authorize/`
}

function tokenEndpoint(cfg: OidcConfig): string {
  return cfg.tokenEndpoint ?? `${cfg.issuer.replace(/\/$/, '')}/token/`
}

/** Fetch userinfo with the access token — Authentik returns email/name/groups. */
export async function fetchUserInfo(
  cfg: OidcConfig,
  accessToken: string,
): Promise<AuthentikUserInfo> {
  const endpoint =
    cfg.userinfoEndpoint ?? `${cfg.issuer.replace(/\/$/, '')}/userinfo/`
  const res = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  })
  if (!res.ok) {
    throw new OidcError(
      'token-exchange-failed',
      `Userinfo fetch failed (${res.status}).`,
    )
  }
  const json = await safeJson(res)
  if (!json || typeof json.sub !== 'string') {
    throw new OidcError('token-exchange-failed', 'Userinfo response malformed.')
  }
  return json as AuthentikUserInfo
}

function parseScratch(raw: string): ScratchPayload | null {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as Record<string, unknown>).verifier === 'string' &&
      typeof (parsed as Record<string, unknown>).state === 'string' &&
      typeof (parsed as Record<string, unknown>).returnTo === 'string' &&
      typeof (parsed as Record<string, unknown>).issuer === 'string'
    ) {
      return parsed as ScratchPayload
    }
  } catch {
    // fall through
  }
  return null
}

async function safeJson(res: Response): Promise<Record<string, unknown> | null> {
  try {
    return (await res.json()) as Record<string, unknown>
  } catch {
    return null
  }
}
